import { ReactNode } from 'react';
import cn from 'classnames';

import styles from './Toggle.module.scss';

export interface ToggleProps {
  /**
   * Whether the toggle is on
   */
  checked: boolean;
  /**
   * Called with the next checked state when the toggle is activated
   */
  onChange: (checked: boolean) => void;
  /**
   * Visible label rendered beside the switch. Keep it non-interactive — the
   * whole toggle is a button, so nested buttons/links are invalid. Render
   * interactive content (e.g. a store picker) as a sibling of `Toggle`.
   */
  label?: ReactNode;
  /**
   * Accessible name, required when no visible `label` is provided
   */
  'aria-label'?: string;
  /**
   * Id of an element that provides the accessible name, taking precedence
   * over both the visible `label` and `aria-label`
   */
  'aria-labelledby'?: string;
  disabled?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

/**
 * Design-system switch: 36×20 track with a 16px knob, neutral when off and
 * `--primary-500` when on.
 */
const Toggle = ({
  checked,
  onChange,
  label,
  disabled = false,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'data-testid': dataTestId,
}: ToggleProps) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      disabled={disabled}
      className={cn(styles.toggle, className)}
      onClick={() => onChange(!checked)}
      data-testid={dataTestId}
    >
      <span className={styles.track} aria-hidden="true">
        <span className={styles.knob} />
      </span>
      {label ? <span className={styles.label}>{label}</span> : null}
    </button>
  );
};

export default Toggle;
